import { useEffect, useRef, useState } from 'react'
import mapValues from 'lodash-es/mapValues'

import { EMPTY_OBJECT } from '../../../../constants/common'

import {
  checkIsDashboardBirdviewFirstLoad,
  getDashboardBirdviewPendingRequestKeys,
  getDashboardBirdviewRequestKeys,
  getDashboardBirdviewRequestKeysSignature,
  getDashboardBirdviewSectionParams,
  getDashboardBirdviewSections,
} from './useDashboardBirdviewSections.utils'

// The effect has no cleanup: cancelling would abort the sections that did not
// change, so the latest key per section drops stale responses instead.
export function useDashboardBirdviewSections({
  getters,
  params,
  sectionParams = EMPTY_OBJECT,
  onReload,
}) {
  const [results, setResults] = useState(EMPTY_OBJECT)
  const [reloadTokens, setReloadTokens] = useState(EMPTY_OBJECT)

  const latestRequestKeysRef = useRef(EMPTY_OBJECT)

  const requestKeys = getDashboardBirdviewRequestKeys({
    getters,
    params,
    reloadTokens,
    sectionParams,
  })
  const requestKeysSignature =
    getDashboardBirdviewRequestKeysSignature(requestKeys)

  function reloadSection(key) {
    onReload?.(key)

    setReloadTokens(tokens => ({ ...tokens, [key]: (tokens[key] ?? 0) + 1 }))
  }

  function reload() {
    onReload?.()

    setReloadTokens(tokens =>
      mapValues(getters, (_, key) => (tokens[key] ?? 0) + 1)
    )
  }

  async function loadSection(key, requestKey) {
    const keyParams = getDashboardBirdviewSectionParams({
      key,
      params,
      sectionParams,
    })

    try {
      return {
        data: await getters[key](keyParams),
        hasError: false,
        requestKey,
      }
    } catch {
      return { data: null, hasError: true, requestKey }
    }
  }

  useEffect(() => {
    getDashboardBirdviewPendingRequestKeys(
      requestKeys,
      latestRequestKeysRef.current
    ).forEach(([key, requestKey]) => {
      latestRequestKeysRef.current = {
        ...latestRequestKeysRef.current,
        [key]: requestKey,
      }

      loadSection(key, requestKey).then(result => {
        if (latestRequestKeysRef.current[key] === requestKey) {
          setResults(prev => ({ ...prev, [key]: result }))
        }
      })
    })
  }, [requestKeysSignature])

  return {
    isFirstLoad: checkIsDashboardBirdviewFirstLoad(results),
    reload,
    sections: getDashboardBirdviewSections({
      requestKeys,
      results,
      onReloadSection: reloadSection,
    }),
  }
}
